import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { api, type User, type AuthStatus, type SetupResponse, type LoginResponse, type SetupTotpResponse, type AuthProvidersResponse } from '@/api/client'

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)
  const needsSetup = ref(false)
  const totpRequired = ref(true)
  const requiresTotp = ref(false)
  const needsTotpSetup = ref(false)
  const canRestartTotpSetup = ref(false)
  const setupData = ref<SetupResponse | null>(null)
  const oauthProviders = ref<string[]>([])

  const isAuthenticated = computed(() => !!user.value)
  const isSuperAdmin = computed(() => user.value?.role === 'superadmin')
  const isAdmin = computed(() => user.value?.role === 'admin' || user.value?.role === 'superadmin')
  const canManageUsers = computed(() => isAdmin.value)
  const canMutate = computed(() => ['superadmin', 'admin'].includes(user.value?.role ?? ''))

  async function checkStatus() {
    try {
      const status = await api.get<AuthStatus>('/auth/status')
      needsSetup.value = status.needsSetup
      totpRequired.value = status.totp_required
    } catch {
      needsSetup.value = false
    }
  }

  async function fetchUser() {
    try {
      user.value = await api.get<User>('/auth/me')
    } catch {
      user.value = null
    }
  }

  async function setup(username: string, password: string) {
    setupData.value = await api.post<SetupResponse>('/auth/setup', { username, password })
    needsSetup.value = false
    return setupData.value
  }

  async function login(username: string, password: string) {
    const res = await api.post<LoginResponse>('/auth/login', { username, password })
    requiresTotp.value = res.requires_totp ?? false
    needsTotpSetup.value = res.needs_totp_setup ?? false
    canRestartTotpSetup.value = res.can_restart_totp_setup ?? false
    return res
  }

  async function setupTotp(force = false) {
    const res = await api.post<SetupTotpResponse>('/auth/setup-totp', force ? { force: true } : undefined)
    canRestartTotpSetup.value = true
    return res
  }

  async function verifyTotp(code: string) {
    const trimmed = code.trim()
    await api.post('/auth/verify-totp', { code: trimmed })
    requiresTotp.value = false
    needsTotpSetup.value = false
    canRestartTotpSetup.value = false
    await fetchUser()
  }

  async function fetchProviders() {
    try {
      const res = await api.get<AuthProvidersResponse>('/auth/providers')
      oauthProviders.value = res.providers
    } catch {
      oauthProviders.value = []
    }
  }

  async function logout() {
    await api.post('/auth/logout')
    user.value = null
    requiresTotp.value = false
    needsTotpSetup.value = false
    canRestartTotpSetup.value = false
  }

  return {
    user,
    needsSetup,
    totpRequired,
    requiresTotp,
    needsTotpSetup,
    canRestartTotpSetup,
    setupData,
    oauthProviders,
    isAuthenticated,
    isSuperAdmin,
    isAdmin,
    canManageUsers,
    canMutate,
    checkStatus,
    fetchUser,
    fetchProviders,
    setup,
    login,
    setupTotp,
    verifyTotp,
    logout,
  }
})
